// Apply IFC's IfcMapConversion (or a user-supplied override) as the
// world transform of a Fragments model so federated IFCs from
// different exporters end up at their real-world position relative to
// each other instead of all stacked at (0,0,0).
//
// Two sources feed the transform:
//
//   1. AUTO baseline derived from IfcMapConversion (Onda A): read once
//      at load time via `model.getCRS()`. Modelos sem MapConversion
//      têm baseline = identidade.
//
//   2. USER override (Onda B): valores manualmente introduzidos no
//      painel "Posição" do sidebar. Quando presente, substitui o
//      baseline completamente (não é delta, é absoluto).
//
// O tipo `GeoPlacement` usa convenção IFC (Eastings, Northings,
// OrthogonalHeight, heading em graus, scale), porque é a forma como o
// utilizador pensa sobre as coordenadas. A conversão para a frame
// Three (Y-up) é feita aqui dentro.
//
// What IfcMapConversion gives us
// ------------------------------
// The transform from the model's local engineering CRS into a map
// CRS (the `IfcProjectedCRS`). It carries:
//
//   eastings, northings    translation (X, Y in the map's right-
//                          handed easting-north plane)
//   orthogonalHeight       translation (Z, vertical, up)
//   xAxisAbscissa,         (cos θ, sin θ): rotation of the X axis
//   xAxisOrdinate          around the vertical axis. Together they
//                          encode a heading.
//   scale                  isotropic scale (usually 1.0, Revit
//                          puts non-unit values when units shift)
//
// IFC space is right-handed Z-up. The Fragments / Three.js scene
// uses right-handed Y-up via a -90° X rotation. To keep the local
// geometry untouched but still apply the map transform in IFC
// space, we conjugate the IFC transformation by that Z-up→Y-up
// rotation:
//
//   position = (eastings, orthogonalHeight, -northings)
//   rotationY = heading (radians, positive CCW around Three +Y)
//   scale = (scale, scale, scale)

import * as THREE from 'three'
import type { CRSData, FragmentsModel } from '@thatopen/fragments'

/** User-facing placement in IFC convention. All values are absolute
 *  (not deltas) and live in the map CRS, except `rotationDeg` which
 *  is the heading of the model's local X axis measured CCW from map
 *  east, in degrees. */
export interface GeoPlacement {
  eastings: number
  northings: number
  orthogonalHeight: number
  /** Heading in degrees, CCW from east. 0° = local X aligned with
   *  map east. 90° = local X points to map north. */
  rotationDeg: number
  /** Isotropic scale. 1 = identity. */
  scale: number
}

export const IDENTITY_PLACEMENT: GeoPlacement = {
  eastings: 0,
  northings: 0,
  orthogonalHeight: 0,
  rotationDeg: 0,
  scale: 1,
}

export interface AppliedGeoTransform {
  /** Was the IfcMapConversion / override actually applied to a
   *  non-identity placement? `false` when both are absent and the
   *  model sits at the origin. */
  applied: boolean
  /** The placement that ended up baked onto model.object. */
  placement: GeoPlacement
  /** Source of the placement. `auto` = derived from
   *  IfcMapConversion; `override` = user-supplied via the sidebar;
   *  `identity` = no MapConversion, no override. */
  source: 'auto' | 'override' | 'identity'
  /** Echoes the IfcMapConversion that was read from the model (null
   *  if the model didn't declare one). Useful for the Diagnóstico
   *  popover. */
  rawMapConversion: CRSData['mapConversion'] | null
}

/** Convert an IfcMapConversion record to a GeoPlacement. Returns null
 *  when the record is missing or degenerate.
 *
 *  `metresPerUnit` é o fator unidade-projeto->metro (1 para metros,
 *  0.001 para mm). O conversor Fragments NORMALIZA a geometria para
 *  METROS, mas o Scale do IfcMapConversion é uma convenção de unidade
 *  (num ficheiro em mm vem 0.001 = 1mm em metros). Aplicá-lo cru como
 *  escala geométrica ao object encolheria o modelo já-em-metros 1000x.
 *  Dividimos por metresPerUnit para obter a escala GEOMÉTRICA real
 *  (1 no caso normal, tanto para m como para mm). O georefWrite faz o
 *  inverso na escrita (onDiskScale = scale * projectMetresPerUnit), por
 *  isso o round-trip fecha em 0.001 para ficheiros em mm. */
/** Diagnóstico da escala do IfcMapConversion face à unidade do projeto.
 *
 *  `placementFromMapConversion` faz `scale = Scale / metresPerUnit`, o que
 *  é a conta certa QUANDO o ficheiro respeita a spec: o Scale converte
 *  comprimentos das unidades do projeto para as do CRS do mapa, logo num
 *  ficheiro em mm com CRS em metros deve vir 0.001, e 0.001/0.001 = 1 (a
 *  geometria do Fragments já vem em metros, fica no tamanho real).
 *
 *  Muitos exportadores, porém, deixam Scale=1 num ficheiro em mm. A conta
 *  dá então 1000 e o mundo do viewer passa a estar em MILÍMETROS: continua
 *  auto-consistente para desenhar, mas tudo o que assume metros passa a
 *  mentir em silêncio (o proj4 do mapa recebe mm como se fossem metros, e
 *  o export KMZ sai 1000x grande e fora do sítio). Isto deteta o caso para
 *  o podermos dizer ao utilizador em vez de adivinhar. */
export interface MapConversionScaleCheck {
  /** Escala geométrica aplicada ao objeto (1 = modelo em tamanho real). */
  geometricScale: number
  /** true quando foge de 1 o suficiente para o mundo deixar de ser metros. */
  suspicious: boolean
  /** O Scale que o ficheiro devia declarar para dar escala geométrica 1
   *  (igual ao fator unidade->metro: 1 para m, 0.001 para mm). */
  expectedFileScale: number
}

export function checkMapConversionScale(
  fileScale: number | null | undefined,
  metresPerUnit = 1,
): MapConversionScaleCheck | null {
  if (!Number.isFinite(fileScale) || (fileScale as number) <= 0) return null
  const unit =
    Number.isFinite(metresPerUnit) && metresPerUnit > 0 ? metresPerUnit : 1
  const geometricScale = (fileScale as number) / unit
  return {
    geometricScale,
    suspicious: Math.abs(geometricScale - 1) > 0.01,
    expectedFileScale: unit,
  }
}

export function placementFromMapConversion(
  mc: CRSData['mapConversion'] | null | undefined,
  metresPerUnit = 1,
): GeoPlacement | null {
  if (!mc) return null
  if (
    !Number.isFinite(mc.eastings) ||
    !Number.isFinite(mc.northings) ||
    !Number.isFinite(mc.orthogonalHeight) ||
    !Number.isFinite(mc.xAxisAbscissa) ||
    !Number.isFinite(mc.xAxisOrdinate) ||
    !Number.isFinite(mc.scale) ||
    mc.scale <= 0
  ) {
    return null
  }
  const unit =
    Number.isFinite(metresPerUnit) && metresPerUnit > 0 ? metresPerUnit : 1
  const rotationDeg =
    (Math.atan2(mc.xAxisOrdinate, mc.xAxisAbscissa) * 180) / Math.PI
  return {
    eastings: mc.eastings,
    northings: mc.northings,
    orthogonalHeight: mc.orthogonalHeight,
    rotationDeg,
    scale: mc.scale / unit,
  }
}

/** O placement de um modelo aberto de um .frag, sem o IFC por trás.
 *
 *  O Fragments guarda o IfcMapConversion tal como vem no ficheiro, mas não
 *  a unidade de comprimento do projecto, que o Scale pressupõe (num IFC em
 *  mm conforme a norma, Scale = 0,001). A geometria do .frag já está em
 *  metros. Sem a unidade não se sabe que escala geométrica o Scale quer
 *  dizer, por isso não se aplica: o modelo fica à escala 1, com a posição e
 *  a rotação declaradas. `escalaIgnorada` é o Scale que ficou por aplicar
 *  (null quando era 1), para o painel o dizer. */
export function placementDeFragments(
  mc: CRSData['mapConversion'] | null | undefined,
): { placement: GeoPlacement | null; escalaIgnorada: number | null } {
  const p = placementFromMapConversion(mc, 1)
  if (!p || !mc) return { placement: null, escalaIgnorada: null }
  const escalaIgnorada = Math.abs(mc.scale - 1) > 1e-9 ? mc.scale : null
  return { placement: { ...p, scale: 1 }, escalaIgnorada }
}

/** True quando as APIs de geometria do Fragments (model.box e as
 *  per-item getBoxes/getPositions) já devolvem coordenadas de MUNDO
 *  (com o transform geo do object aplicado), observado no fragments
 *  3.4.3. Aplicar matrixWorld em cima duplicava o transform (câmara,
 *  painel Coordenadas e pegada iam parar a 2× da posição real).
 *  Heurística: o centro do model.box cru fica mais perto da posição do
 *  object do que o centro re-transformado; para transform identidade
 *  as duas interpretações coincidem e o resultado é indiferente. */
export function fragmentsReturnsWorld(model: FragmentsModel): boolean {
  const raw = model.box
  if (!raw || raw.isEmpty()) return true
  model.object.updateMatrixWorld(true)
  const applied = raw.clone().applyMatrix4(model.object.matrixWorld)
  const pos = model.object.position
  const cRaw = raw.getCenter(new THREE.Vector3())
  const cApp = applied.getCenter(new THREE.Vector3())
  return cRaw.distanceTo(pos) <= cApp.distanceTo(pos)
}

// ---------------------------------------------------------------------------
// Cache da caixa de geometria (união dos getBoxes por elemento).
//
// O `model.box` do motor vem INFLADO (medido no exemplo: span E de 41017
// contra 9028 reais, 4.5x), o que desloca a pegada no mapa, o centro da
// vista, a cota do chão do basemap e o delta de federação. A extensão
// real só se obtém pela união das caixas por elemento, que é assíncrona;
// mas vários consumidores do resolveWorldBox são SÍNCRONOS (um deles
// corre durante o render do MapPanel). Resolução: prima-se a união uma
// vez por modelo depois do load (primeGeometryBoxCache) e o
// resolveWorldBox passa a servi-la de forma síncrona.
//
// Staleness: a caixa é guardada com a matrixWorld de captura. Se o
// placement mudar (override manual, federação), o resolveWorldBox aplica
// o DELTA de matrizes à caixa cacheada (aproximação AABB, exata para
// translações) e dispara um re-prime em fundo que a substitui pela
// união fresca. WeakMap: liberta-se com o próprio modelo.
// ---------------------------------------------------------------------------

interface GeomBoxCacheEntry {
  box: THREE.Box3
  matrix: THREE.Matrix4
}
const geomBoxCache = new WeakMap<object, GeomBoxCacheEntry>()
const geomBoxPriming = new WeakSet<object>()

/** Igualdade de matrizes com tolerâncias adequadas: translação (12..14)
 *  a 1e-6 (mundos georreferenciados vivem em 1e5..1e9), resto a 1e-9. */
function sameMatrix(a: THREE.Matrix4, b: THREE.Matrix4): boolean {
  const ea = a.elements
  const eb = b.elements
  for (let i = 0; i < 16; i++) {
    const tol =
      i >= 12 && i < 15 ? 1e-6 + 1e-12 * Math.abs(ea[i]) : 1e-9
    if (Math.abs(ea[i] - eb[i]) > tol) return false
  }
  return true
}

/** União das caixas por elemento (mundo), sem aparar. Null quando a API
 *  não está disponível ou não devolve nenhuma caixa finita. */
async function computeGeometryUnionBox(
  model: FragmentsModel,
): Promise<THREE.Box3 | null> {
  const src = model as unknown as Partial<BoxSource>
  if (!src.getItemsIdsWithGeometry || !src.getBoxes) return null
  let ids: number[]
  try {
    ids = await src.getItemsIdsWithGeometry()
  } catch {
    return null
  }
  if (!ids || ids.length === 0) return null
  let boxes: THREE.Box3[]
  try {
    boxes = await src.getBoxes(ids)
  } catch {
    return null
  }
  const union = new THREE.Box3()
  let any = false
  for (const b of boxes ?? []) {
    if (!b || !Number.isFinite(b.min.x) || !Number.isFinite(b.max.x)) continue
    union.union(b)
    any = true
  }
  return any ? union : null
}

/** A pegada do modelo em planta, ORIENTADA como o modelo: a caixa no
 *  referencial do próprio modelo (as caixas por elemento levadas para lá
 *  pela inversa da matriz de mundo) e os seus quatro cantos levados de
 *  volta ao mundo. Num modelo rodado no mapa dá um rectângulo rodado, e
 *  não a caixa alinhada aos eixos do mapa, que é maior. Devolve [E, N]
 *  em metros (E = x, N = -z), ou null. */
export async function pegadaEmPlanta(model: FragmentsModel): Promise<Array<[number, number]> | null> {
  const src = model as unknown as Partial<BoxSource>
  if (!src.getItemsIdsWithGeometry || !src.getBoxes) return null
  let boxes: THREE.Box3[]
  try {
    const ids = await src.getItemsIdsWithGeometry()
    if (!ids || ids.length === 0) return null
    boxes = await src.getBoxes(ids)
  } catch {
    return null
  }
  model.object.updateMatrixWorld(true)
  const m = model.object.matrixWorld
  const inv = m.clone().invert()
  const local = new THREE.Box3()
  const tmp = new THREE.Box3()
  // As caixas do Fragments vêm em mundo (ver fragmentsReturnsWorld);
  // levam-se ao referencial do modelo. Decide-se uma vez: dentro do ciclo
  // cada chamada actualizava a árvore inteira do modelo, por caixa.
  const emMundo = fragmentsReturnsWorld(model)
  for (const b of boxes ?? []) {
    if (!b || !Number.isFinite(b.min.x) || !Number.isFinite(b.max.x)) continue
    local.union(emMundo ? tmp.copy(b).applyMatrix4(inv) : tmp.copy(b))
  }
  if (local.isEmpty()) return null
  const y = local.min.y
  const cantos = [
    new THREE.Vector3(local.min.x, y, local.min.z),
    new THREE.Vector3(local.max.x, y, local.min.z),
    new THREE.Vector3(local.max.x, y, local.max.z),
    new THREE.Vector3(local.min.x, y, local.max.z),
  ]
  return cantos.map((c) => {
    c.applyMatrix4(m)
    return [c.x, -c.z] as [number, number]
  })
}

/** Prima a cache da caixa de geometria para este modelo. Chamar depois
 *  do load (useViewer) e sempre que se queira refrescar; falha em
 *  silêncio (a cache simplesmente não existe e o resolveWorldBox cai no
 *  comportamento antigo). */
export async function primeGeometryBoxCache(
  model: FragmentsModel,
): Promise<void> {
  if (geomBoxPriming.has(model)) return
  geomBoxPriming.add(model)
  try {
    const union = await computeGeometryUnionBox(model)
    if (union) {
      model.object.updateMatrixWorld(true)
      geomBoxCache.set(model, {
        box: union,
        matrix: model.object.matrixWorld.clone(),
      })
    }
  } finally {
    geomBoxPriming.delete(model)
  }
}

/** World-space bounding box of a model.
 *
 *  Com a cache primada devolve a EXTENSÃO REAL da geometria (união das
 *  caixas por elemento); sem cache mantém o comportamento histórico
 *  (model.box + ambiguidade local/mundo via fragmentsReturnsWorld), que
 *  pode vir inflado pelo motor. */
export function resolveWorldBox(model: FragmentsModel): THREE.Box3 | null {
  const cached = geomBoxCache.get(model)
  if (cached) {
    model.object.updateMatrixWorld(true)
    const current = model.object.matrixWorld
    if (sameMatrix(current, cached.matrix)) return cached.box.clone()
    // Placement mudou desde a captura: serve já uma aproximação por
    // delta (exata para translações) e refresca a união em fundo.
    const delta = current
      .clone()
      .multiply(cached.matrix.clone().invert())
    void primeGeometryBoxCache(model)
    return cached.box.clone().applyMatrix4(delta)
  }
  const raw = model.box
  if (!raw || raw.isEmpty()) return null
  if (fragmentsReturnsWorld(model)) return raw.clone()
  model.object.updateMatrixWorld(true)
  return raw.clone().applyMatrix4(model.object.matrixWorld)
}

/** Interface mínima do modelo Fragments usada pela caixa robusta. */
interface BoxSource {
  getItemsIdsWithGeometry(): Promise<number[]>
  getBoxes(ids: number[]): Promise<THREE.Box3[]>
}

/** Mediana de um array (não vazio). */
function median(arr: number[]): number {
  const s = [...arr].sort((a, b) => a - b)
  const m = s.length >> 1
  return s.length % 2 ? s[m] : (s[m - 1] + s[m]) / 2
}

/** Núcleo PURO da caixa robusta: dadas as caixas por elemento e a
 *  extensão da caixa cheia, apara os centros-outlier (por MAD, 6x, com
 *  piso de 2% da extensão para MAD~0) e devolve a união dos que ficam.
 *  Devolve null quando há menos de 6 caixas úteis ou quando o corte
 *  removeria mais de metade (geometria dispersa, não outliers), para o
 *  chamador cair na caixa cheia. Testável sem modelo. */
export function robustBoxFromElementBoxes(
  boxes: THREE.Box3[],
  fullExtent: { x: number; y: number; z: number },
): THREE.Box3 | null {
  const valid = boxes.filter((b) => b && !b.isEmpty())
  if (valid.length < 6) return null
  const axes = ['x', 'y', 'z'] as const
  const centre = (b: THREE.Box3, a: (typeof axes)[number]) =>
    (b.min[a] + b.max[a]) / 2
  const med: Record<string, number> = {}
  const thr: Record<string, number> = {}
  for (const a of axes) {
    const cs = valid.map((b) => centre(b, a))
    const mc = median(cs)
    med[a] = mc
    const mad = median(cs.map((c) => Math.abs(c - mc)))
    thr[a] = Math.max(6 * mad, 0.02 * fullExtent[a])
  }
  const kept = valid.filter((b) =>
    axes.every((a) => Math.abs(centre(b, a) - med[a]) <= thr[a]),
  )
  if (kept.length < valid.length * 0.5) return null
  const robust = new THREE.Box3()
  for (const b of kept) robust.union(b)
  return robust.isEmpty() ? null : robust
}

/** Caixa ROBUSTA para ENQUADRAR a câmara. Como resolveWorldBox, mas
 *  ignora elementos-outlier (marcos de federação, textos 3D, anotações)
 *  cujo centro está muito longe do grosso da geometria: sozinhos, esses
 *  esticam a bounding box e fazem a câmara apontar para espaço vazio,
 *  deixando o modelo pequeno e a "parecer inclinado".
 *
 *  Usa as caixas por elemento (getBoxes) e apara por MAD (desvio absoluto
 *  mediano, robusto a outliers) sobre os centros, por eixo. O corte é
 *  generoso (6 x MAD): só descarta o que está mesmo fora do aglomerado, e
 *  nunca corta mais de metade (dois edifícios afastados => MAD grande =>
 *  não corta nada). Assíncrona; cai para resolveWorldBox quando não há
 *  elementos suficientes ou a API falha. */
export async function resolveRobustFitBox(
  model: FragmentsModel,
): Promise<THREE.Box3 | null> {
  const full = resolveWorldBox(model)
  const src = model as unknown as Partial<BoxSource>
  if (!src.getItemsIdsWithGeometry || !src.getBoxes) return full
  let ids: number[]
  try {
    ids = await src.getItemsIdsWithGeometry()
  } catch {
    return full
  }
  // Poucos elementos: aparar não é fiável. Muitos: o custo/latência não
  // compensa e o impacto de um outlier é proporcionalmente menor.
  if (!ids || ids.length < 6 || ids.length > 60_000) return full
  let boxes: THREE.Box3[]
  try {
    boxes = await src.getBoxes(ids)
  } catch {
    return full
  }
  const fullExtent = full
    ? { x: full.max.x - full.min.x, y: full.max.y - full.min.y, z: full.max.z - full.min.z }
    : { x: 0, y: 0, z: 0 }
  return robustBoxFromElementBoxes(boxes ?? [], fullExtent) ?? full
}

/** Caixa de mundo pela UNIÃO das caixas por elemento (getBoxes), sem
 *  aparar nada. É a extensão REAL da geometria.
 *
 *  Porque não as outras duas, para ANCORAR (pegada no mapa, origem do
 *  KMZ), medido no exemplo buildingSMART:
 *  - `resolveWorldBox` usa `model.box`, que o motor infla: span E de
 *    41017 contra 9028 reais (4.5x), o que desloca o centro e desenha
 *    uma pegada muito maior que o edifício.
 *  - `resolveRobustFitBox` apara outliers por MAD: serve para ENQUADRAR
 *    a câmara (aparar a mais é benigno, só afeta o zoom), mas corta
 *    extensão legítima (span N de 46324 para 12541) e deslocaria a
 *    âncora ~17 m. Para ancorar isso é destrutivo.
 *  A união dá desvio zero face à geometria e é a escolha certa quando o
 *  que interessa é ONDE o modelo está, não como enquadrá-lo.
 *
 *  Assíncrona; cai para resolveWorldBox quando o motor não expõe as
 *  caixas por elemento. */
export async function resolveGeometryBox(
  model: FragmentsModel,
): Promise<THREE.Box3 | null> {
  const union = await computeGeometryUnionBox(model)
  return union ?? resolveWorldBox(model)
}

/** Recentre translation que o conversor Fragments aplicou à geometria
 *  deste modelo (frame three), lida de model.getCoordinates() no load e
 *  guardada em userData pelo useViewer. [0,0,0] quando desconhecida. */
export function modelRecentre(
  model: FragmentsModel,
): [number, number, number] {
  const t = model.object.userData.vn2rRecentre as
    | [number, number, number]
    | undefined
  return Array.isArray(t) && t.length === 3 ? t : [0, 0, 0]
}

/** Apply a GeoPlacement to `model.object`. Idempotent: always sets
 *  position/rotation/scale from the placement, never accumulates.
 *
 *  O placement aplica-se às coordenadas do FICHEIRO IFC. O conversor
 *  Fragments recentra a geometria (mesmo offsets modestos) e devolve a
 *  translação t em getCoordinates(); sem a desfazer, o modelo ficava
 *  deslocado por R(θ)·t face à posição verdadeira (dezenas de metros e
 *  aparência de rodado vs. plataformas que trabalham nos bytes
 *  originais). Compomos T(E,h,-N) · R(θ) · S · T(-t). */
export function applyGeoPlacement(
  model: FragmentsModel,
  placement: GeoPlacement,
): void {
  const rotationY = (placement.rotationDeg * Math.PI) / 180
  const scale = placement.scale || 1
  const t = modelRecentre(model)
  const off = new THREE.Vector3(-t[0], -t[1], -t[2])
    .multiplyScalar(scale)
    .applyEuler(new THREE.Euler(0, rotationY, 0))
  model.object.position.set(
    placement.eastings + off.x,
    placement.orthogonalHeight + off.y,
    -placement.northings + off.z,
  )
  model.object.rotation.set(0, rotationY, 0)
  model.object.scale.set(scale, scale, scale)
  model.object.updateMatrixWorld(true)
}

/**
 * Apply the auto-derived IfcMapConversion (if present) to
 * `model.object`. Returns metadata about what was applied so the
 * loader can save the baseline placement to the store (so the
 * sidebar override editor knows what to compare against).
 *
 * The transform is BAKED onto the Three.js Object3D, so subsequent
 * operations (camera fit, raycasting, point-of-interest math) see
 * the geo-referenced coordinates without further conversion.
 *
 * Idempotent across re-applies on the same model.
 */
export function applyGeoTransform(
  model: FragmentsModel,
  crs: CRSData | null,
  metresPerUnit = 1,
): AppliedGeoTransform {
  const baseline = placementFromMapConversion(crs?.mapConversion, metresPerUnit)
  if (!baseline) {
    applyGeoPlacement(model, IDENTITY_PLACEMENT)
    return {
      applied: false,
      placement: IDENTITY_PLACEMENT,
      source: 'identity',
      rawMapConversion: crs?.mapConversion ?? null,
    }
  }
  applyGeoPlacement(model, baseline)
  return {
    applied: true,
    placement: baseline,
    source: 'auto',
    rawMapConversion: crs?.mapConversion ?? null,
  }
}

/** Returns true when `a` differs from `b` by more than the tolerance
 *  (1e-6 on rotation/scale, 1mm on translations). Used by the
 *  sidebar to decide whether to show the "override" badge. */
export function placementsDiffer(a: GeoPlacement, b: GeoPlacement): boolean {
  const T = 1e-3 // 1mm
  const R = 1e-6
  return (
    Math.abs(a.eastings - b.eastings) > T ||
    Math.abs(a.northings - b.northings) > T ||
    Math.abs(a.orthogonalHeight - b.orthogonalHeight) > T ||
    Math.abs(a.rotationDeg - b.rotationDeg) > R ||
    Math.abs(a.scale - b.scale) > R
  )
}

/** Build the same matrix `applyGeoPlacement` would compose, without
 *  mutating anything. Useful for projecting points / measuring
 *  distances against world coords without touching the scene.
 *  `recentre` = translação do conversor (ver modelRecentre). */
export function placementToMatrix(
  p: GeoPlacement,
  recentre: [number, number, number] = [0, 0, 0],
): THREE.Matrix4 {
  const rotationY = (p.rotationDeg * Math.PI) / 180
  const scale = p.scale || 1
  const off = new THREE.Vector3(-recentre[0], -recentre[1], -recentre[2])
    .multiplyScalar(scale)
    .applyEuler(new THREE.Euler(0, rotationY, 0))
  const m = new THREE.Matrix4()
  m.compose(
    new THREE.Vector3(
      p.eastings + off.x,
      p.orthogonalHeight + off.y,
      -p.northings + off.z,
    ),
    new THREE.Quaternion().setFromEuler(new THREE.Euler(0, rotationY, 0)),
    new THREE.Vector3(scale, scale, scale),
  )
  return m
}
